import { Image } from 'expo-image';
import { Link, Redirect } from 'expo-router';
import { ArrowRight } from 'lucide-react-native';
import * as React from 'react';
import { ScrollView, useWindowDimensions, View, type LayoutChangeEvent } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BrandBackdrop } from '@/components/brand/brand-backdrop';
import { BrandLabel } from '@/components/brand/brand-label';
import { PosterTelemetry } from '@/components/brand/poster-telemetry';
import { RouteLinePreview } from '@/components/brand/route-line-preview';
import { useSession } from '@/components/session-provider';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { MotionView } from '@sixam/ui/motion';
import { Text } from '@sixam/ui/text';
import { brandAssets, mediaAssets } from '@/lib/brand-assets';
import { fetchLandingEvent, type LandingEventData } from '@/lib/landing-event';
import { landingRunPresentation } from '@/lib/landing-event-core';

const HORIZONTAL_LOGO_ASPECT_RATIO = 1600 / 347;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getViewportFlags(width: number, height: number) {
  const isLandscape = width > height;
  const isTablet = width >= 700;

  return {
    isLandscape,
    isNarrow: width < 380,
    isShort: height < 760,
    isTablet,
    isTiny: height < 610,
    isVeryShort: height < 680,
    splitLayout: isLandscape || isTablet,
  };
}

function getMainGap(isTiny: boolean, isVeryShort: boolean, isShort: boolean) {
  if (isTiny) {
    return 12;
  }

  if (isVeryShort) {
    return 16;
  }

  if (isShort) {
    return 20;
  }

  return 28;
}

function getCopyGap(isTiny: boolean, isShort: boolean) {
  if (isTiny) {
    return 12;
  }

  if (isShort) {
    return 16;
  }

  return 22;
}

function getLogoDisplayWidth(contentWidth: number, splitLayout: boolean) {
  if (splitLayout) {
    return clamp(contentWidth * 0.24, 170, 238);
  }

  return clamp(contentWidth * 0.68, 218, 258);
}

function getRouteHeight(
  availableHeight: number,
  splitLayout: boolean,
  isTiny: boolean,
  isVeryShort: boolean
) {
  if (splitLayout) {
    return clamp(availableHeight * 0.22, 70, 142);
  }

  if (isTiny) {
    return clamp(availableHeight * 0.12, 64, 124);
  }

  if (isVeryShort) {
    return clamp(availableHeight * 0.14, 64, 124);
  }

  return clamp(availableHeight * 0.17, 64, 124);
}

function getTitleSize(
  width: number,
  splitLayout: boolean,
  isLandscape: boolean,
  isNarrow: boolean,
  isTiny: boolean,
  isVeryShort: boolean
) {
  if (splitLayout) {
    const min = isLandscape ? 26 : 30;
    const max = isLandscape ? 34 : 52;
    const scale = isLandscape ? 0.032 : 0.044;

    return clamp(width * scale, min, max);
  }

  const min = isTiny ? 24 : 28;
  const max = isVeryShort ? 34 : 42;
  const scale = isNarrow ? 0.075 : 0.085;

  return clamp(width * scale, min, max);
}

function getActionGroupWidth(contentWidth: number, mainGap: number, splitLayout: boolean) {
  if (splitLayout) {
    return Math.min(contentWidth, Math.max(280, (contentWidth - mainGap) * 0.55));
  }

  return contentWidth;
}

function getBodySize(isTiny: boolean, isVeryShort: boolean) {
  if (isTiny) {
    return 12.5;
  }

  if (isVeryShort) {
    return 14;
  }

  return 16;
}

function getLandingLayout(
  width: number,
  height: number,
  insets: Readonly<{ top: number; bottom: number }>
) {
  const flags = getViewportFlags(width, height);
  const horizontalPadding = clamp(width * 0.055, 18, 40);
  const topPadding = insets.top + clamp(height * 0.006, 4, flags.splitLayout ? 10 : 8);
  const bottomPadding = insets.bottom + clamp(height * 0.014, 8, 18);
  const availableHeight = Math.max(360, height - topPadding - bottomPadding);
  const logoTopMargin = clamp(height * 0.018, 8, flags.splitLayout ? 18 : 22);
  const mainGap = getMainGap(flags.isTiny, flags.isVeryShort, flags.isShort);
  const copyGap = getCopyGap(flags.isTiny, flags.isShort);
  const contentWidth = width - horizontalPadding * 2;
  const logoWidth = getLogoDisplayWidth(contentWidth, flags.splitLayout);
  const routeHeight = getRouteHeight(
    availableHeight,
    flags.splitLayout,
    flags.isTiny,
    flags.isVeryShort
  );
  const titleSize = getTitleSize(
    width,
    flags.splitLayout,
    flags.isLandscape,
    flags.isNarrow,
    flags.isTiny,
    flags.isVeryShort
  );
  const bodySize = getBodySize(flags.isTiny, flags.isVeryShort);
  const actionGap = flags.isNarrow ? 8 : 12;
  const actionGroupWidth = getActionGroupWidth(contentWidth, mainGap, flags.splitLayout);
  const actionButtonWidth = (actionGroupWidth - actionGap) / 2;
  const compactActions = actionButtonWidth < 230;
  const runToActionGap = 28;

  return {
    ...flags,
    actionButtonWidth,
    actionGap,
    actionGroupWidth,
    actionHeight: flags.isTiny ? 44 : 48,
    actionLetterSpacing: compactActions ? 0.4 : 1.8,
    actionTextSize: compactActions ? 11 : 12,
    availableHeight,
    bodyLineHeight: bodySize * (flags.isTiny ? 1.22 : 1.35),
    bodySize,
    bottomPadding,
    contentWidth,
    copyGap,
    horizontalPadding,
    logoHeight: logoWidth / HORIZONTAL_LOGO_ASPECT_RATIO,
    logoTopMargin,
    logoWidth,
    mainGap,
    routeHeight,
    runToActionGap,
    titleLineHeight: titleSize * 0.98,
    titleLines: !flags.splitLayout && width < 440 ? 3 : 2,
    titleSize,
    topPadding,
  };
}

export default function LandingScreen() {
  const { session, isLoading } = useSession();
  const { height, width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  // The window is taller than the space this screen gets on Android, where the
  // status bar strip sits above it. Sizing from the window overflows the
  // viewport, and scrolling is disabled here by design, so the overflow is
  // simply unreachable. Measure what we are actually given instead.
  const [viewportHeight, setViewportHeight] = React.useState(height);
  const layout = getLandingLayout(width, viewportHeight, insets);

  const measure = React.useCallback((event: LayoutChangeEvent) => {
    const measured = event.nativeEvent.layout.height;
    setViewportHeight((current) => (Math.abs(current - measured) > 1 ? measured : current));
  }, []);
  const [landingEvent, setLandingEvent] = React.useState<LandingEventData | null>(null);

  React.useEffect(() => {
    if (isLoading || session) return;
    let active = true;
    void fetchLandingEvent()
      .then((data) => {
        if (active) setLandingEvent(data);
      })
      .catch(() => {
        // The designed Monday run remains visible when public data is unavailable.
      });
    return () => {
      active = false;
    };
  }, [isLoading, session]);

  const run = landingRunPresentation(
    landingEvent?.event ?? null,
    landingEvent?.route ?? null,
    landingEvent?.tenant?.timezone
  );

  if (!isLoading && session) {
    return <Redirect href="/app" />;
  }

  return (
    <BrandBackdrop
      source={mediaAssets.homeViewBg}
      blurTint="systemUltraThinMaterialLight"
      className="bg-white"
      initialBlurIntensity={24}
      scrimOpacity={0.54}
    >
      <ScrollView
        bounces={false}
        contentInsetAdjustmentBehavior="automatic"
        className="flex-1"
        contentContainerStyle={{
          // flexGrow fills exactly the viewport, where a fixed minHeight plus
          // padding added up to more than the screen and pushed the buttons out
          // of reach.
          flexGrow: 1,
          paddingBottom: layout.bottomPadding,
          paddingHorizontal: layout.horizontalPadding,
          paddingTop: layout.topPadding,
        }}
        onLayout={measure}
        scrollEnabled={false}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={{
            flex: 1,
            gap: layout.mainGap,
          }}
        >
          <MotionView
            preset="fade"
            className="flex-row items-start justify-center"
            style={{
              alignSelf: 'stretch',
              marginTop: layout.logoTopMargin,
            }}
          >
            <Image
              source={brandAssets.logoReverse}
              style={{ height: layout.logoHeight, width: layout.logoWidth }}
              contentFit="contain"
              accessibilityLabel="6am BKK Bangkok Run Club"
            />
          </MotionView>

          <MotionView
            delay={160}
            preset="fade"
            style={{
              alignItems: 'stretch',
              flex: 1,
              gap: layout.runToActionGap,
              justifyContent: 'flex-end',
            }}
          >
            <View
              style={{
                alignItems: 'stretch',
                flexDirection: layout.splitLayout ? 'row' : 'column',
                gap: layout.splitLayout ? layout.mainGap : layout.copyGap,
              }}
            >
              <View
                style={{
                  flex: layout.splitLayout ? 0.9 : undefined,
                  gap: layout.isTiny ? 10 : 14,
                  justifyContent: 'flex-end',
                }}
              >
                <PosterTelemetry
                  compact
                  metrics={run.metrics}
                  className="min-w-[220px]"
                  style={{
                    maxWidth: layout.splitLayout ? 360 : 416,
                    width: layout.splitLayout ? '100%' : clamp(layout.contentWidth, 220, 416),
                  }}
                />
                <RouteLinePreview
                  className="max-w-[720px] self-stretch"
                  geometry={landingEvent?.route?.version.geometry}
                  name={landingEvent?.route?.route.name ?? run.title}
                  style={{ height: layout.routeHeight }}
                />
              </View>

              <View
                style={{
                  flex: layout.splitLayout ? 1.1 : undefined,
                  justifyContent: 'flex-end',
                }}
              >
                <View style={{ gap: layout.isTiny ? 8 : 12 }}>
                  <BrandLabel>Next event</BrandLabel>
                  <Text
                    adjustsFontSizeToFit
                    minimumFontScale={0.86}
                    numberOfLines={layout.titleLines}
                    variant="h1"
                    className="max-w-[720px] normal-case text-white"
                    style={{ fontSize: layout.titleSize, lineHeight: layout.titleLineHeight }}
                  >
                    {run.title}
                  </Text>
                  <Text
                    adjustsFontSizeToFit
                    minimumFontScale={0.84}
                    numberOfLines={3}
                    className="max-w-[560px] font-sans text-white/75"
                    style={{ fontSize: layout.bodySize, lineHeight: layout.bodyLineHeight }}
                  >
                    {run.description}
                  </Text>
                </View>
              </View>
            </View>

            <View
              className="flex-row"
              style={{
                alignSelf: layout.splitLayout ? 'flex-end' : 'stretch',
                gap: layout.actionGap,
                width: layout.actionGroupWidth,
              }}
            >
              <Link href="/(public)/sign-in" asChild>
                <Button style={{ height: layout.actionHeight, width: layout.actionButtonWidth }}>
                  <Text
                    adjustsFontSizeToFit
                    minimumFontScale={0.76}
                    numberOfLines={1}
                    style={{
                      fontSize: layout.actionTextSize,
                      letterSpacing: layout.actionLetterSpacing,
                    }}
                  >
                    Sign in
                  </Text>
                  <Icon as={ArrowRight} />
                </Button>
              </Link>
              <Link href="/(public)/sign-up" asChild>
                <Button
                  variant="outline"
                  style={{ height: layout.actionHeight, width: layout.actionButtonWidth }}
                >
                  <Text
                    adjustsFontSizeToFit
                    minimumFontScale={0.7}
                    numberOfLines={1}
                    style={{
                      fontSize: layout.actionTextSize,
                      letterSpacing: layout.actionLetterSpacing,
                    }}
                  >
                    Sign up
                  </Text>
                </Button>
              </Link>
            </View>
          </MotionView>
        </View>
      </ScrollView>
    </BrandBackdrop>
  );
}
